import { Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  numericFieldErrorText,
  VALUELESS_KINDS,
  type BlockMember,
  type IntensityKind,
  type KindLabels,
} from "@jackedlogs/shared";

import { Chip } from "../primitives/Chip";
import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";
import { ICON_HIT, valueFieldKind } from "./composerSetTableShared";

// How this set is BOUNDED (HI-1). Three options, one radiogroup: the reps
// set that has always existed, a clock-bounded bout, and an open bout the
// athlete ends. `undefined` is deliberately the first option rather than an
// absent state — "Reps" is a choice the author can come back to, and a set
// with no chip selected would read as broken.
const BOUNDS = [
  { kind: undefined, key: "program.boundReps", fallback: "Reps" },
  { kind: "countdown" as const, key: "program.boundTime", fallback: "Time" },
  { kind: "countup" as const, key: "program.boundOpen", fallback: "Open" },
];

// One interleaved set cell inside ComposerSetTable's metric table (1A·1B·2A·2B).
// Split out of ComposerSetTable for size (SP-COMPOSERSETTABLE) — a pure move:
// the table still owns interleaveRounds and the open-note state, and hands this
// component one cell's worth of it.
//
// `noteOpen` is a BOOLEAN for THIS cell, not the table's `noteOpen: string |
// null` cell key, and `onToggleNote`/`onUpdateRow`/`onRemoveRound` arrive
// already bound to this cell's coordinates. That is deliberate: the row cannot
// address a cell other than its own, which is the failure this split had to
// avoid.
export function ComposerSetRow({
  row,
  label,
  headerKind,
  kindLabels,
  legacyNotes,
  showRemoveRound,
  noteOpen,
  timeOpen,
  onToggleNote,
  onUpdateRow,
  onRemoveRound,
  styles,
  theme,
}: {
  row: BlockMember["setRows"][number];
  label: string;
  headerKind: IntensityKind;
  kindLabels: KindLabels;
  // The legacy per-set note COLUMN exists at all (table-wide decision).
  legacyNotes: boolean;
  // maxRounds > 1 upstream: there is a round to drop without orphaning a
  // member. False renders the fixed-width spacer that keeps the column aligned.
  showRemoveRound: boolean;
  noteOpen: boolean;
  // The TIME axis lines are revealed table-wide, by one control under the
  // table (HI-1). Not a per-row icon: this table's columns flex to fit a
  // narrow edit-in-place card, and its own header warns that a fixed box
  // inside them squeezes the value select until it wraps (PE-17).
  timeOpen: boolean;
  onToggleNote: () => void;
  onUpdateRow: (patch: Partial<BlockMember["setRows"][number]>) => void;
  onRemoveRound: () => void;
  styles: ComposerStyles;
  theme: UiTheme;
}) {
  const { t } = useTranslation();

  const rowValueless = VALUELESS_KINDS.has(row.intensityKind);
  const workRaw = (row.workDurationSec ?? "").trim();
  // HI-5. A clock-bounded set keeps its rep prescription: "10 squat con 3
  // secondi in basso" is a real thing to ask for, and SetPrescription has
  // always carried `reps` and `work` together. What his rule forbids (§10
  // punto 2 — NOT §9.5, which is the protocol table that only foreshadows it,
  // his words: «per un atleta potrebbe essere 2 secondi e per un altro 45»)
  // is the AMRAP FLOOR on a bout — as many as you can, in a fixed time, is a
  // contradiction. The build enforces exactly that and no more
  // (buildWorkForCell's amrapAllowed); the cell used to be replaced by the
  // word "Time", which enforced far more than the rule and took the ordinary
  // rep count with it.
  const amrapWithTime =
    row.workKind === "countdown" && /\+\s*$/.test(row.reps ?? "");
  const repsError =
    numericFieldErrorText("prescribedReps", row.reps, t) ??
    // ⚠️ Only once the bout HAS a length. With the seconds cell still empty
    // its own "How long?" is the thing to fix first, and this line would name
    // a duration that does not exist yet. The save refuses either way — an
    // empty countdown never gets past timeAxisError.
    (amrapWithTime && workRaw !== ""
      ? t("program.amrapNotWithTime", {
          secs: workRaw,
          defaultValue: "A {{secs}}s bout can't also be an AMRAP: a plan can't ask how many you'll manage. Drop the + or the time.",
        })
      : null);
  const repsInvalid = repsError !== null;
  const valueInvalid =
    !rowValueless &&
    numericFieldErrorText(
      valueFieldKind(row.intensityKind),
      row.intensityValue,
      t,
    ) !== null;
  const diverges = row.intensityKind !== headerKind;
  // Per-FIELD, not folded into the table's shared line. That line is
  // first-error-wins for the whole table, so a second bad cell would be a
  // red border with no text anywhere — colour-only, which is a floor we do
  // not go under. Both seconds fields can be wrong at once and both say so.
  const workError =
    numericFieldErrorText("seconds", workRaw, t) ??
    // A blank cell is valid everywhere else — it means "not prescribed".
    // On a countdown it is the one question the bound cannot answer for
    // itself, and the save refuses it, so the field has to say so here
    // rather than let the refusal arrive with nothing marked.
    (row.workKind === "countdown" && workRaw === ""
      ? t("program.workSecondsRequired", { defaultValue: "How long?" })
      : null);
  const restError = numericFieldErrorText("seconds", row.restSec ?? "", t);

  return (
    <View style={{ gap: 6 }}>
      <View style={styles.setRow}>
        <Text style={styles.setNum}>{label}</Text>
        <TextInput
          style={[
            styles.cellInput,
            styles.colReps,
            repsInvalid && styles.cellInputInvalid,
          ]}
          value={row.reps}
          onChangeText={(v) => onUpdateRow({ reps: v })}
          // numbers-and-symbols keyboard: the AMRAP suffix ("5+")
          // needs the + key, which number-pad hides on Android.
          keyboardType="numbers-and-punctuation"
          accessibilityLabel={t("program.reps", { defaultValue: "Reps" })}
        />
        {rowValueless ? (
          <View style={[styles.colValue, { alignItems: "center" }]}>
            {diverges ? (
              <Text style={styles.headText}>
                {kindLabels[row.intensityKind]}
              </Text>
            ) : null}
          </View>
        ) : (
          <TextInput
            style={[
              styles.cellInput,
              styles.colValue,
              valueInvalid && styles.cellInputInvalid,
            ]}
            value={row.intensityValue}
            onChangeText={(v) => onUpdateRow({ intensityValue: v })}
            keyboardType="decimal-pad"
            accessibilityLabel={kindLabels[row.intensityKind]}
            placeholder={diverges ? kindLabels[row.intensityKind] : undefined}
          />
        )}
        {/* Legacy per-set note cell: visible only while this row
             still has one (or its editor is open mid-erase). New
             notes are authored per-exercise in the member head. */}
        {legacyNotes ? (
          row.note.trim() !== "" || noteOpen ? (
            <IconButton
              theme={theme}
              name="note"
              active={noteOpen}
              expanded={noteOpen}
              accessibilityLabel={t("composer.setNote", {
                defaultValue: "Set note",
              })}
              onPress={onToggleNote}
              hitSlop={ICON_HIT}
            />
          ) : (
            <View style={styles.colNote} />
          )
        ) : null}
        {/* "–" drops the whole ROUND across every member (logger
             parity: 1A and 1B leave together); hidden when there is
             no round to drop without orphaning a member — then a
             fixed-width spacer keeps the column aligned. */}
        {showRemoveRound ? (
          <IconButton
            theme={theme}
            name="minus"
            variant="destructive"
            accessibilityLabel={t("program.removeSet", {
              defaultValue: "Remove set",
            })}
            onPress={onRemoveRound}
            hitSlop={ICON_HIT}
          />
        ) : (
          <View style={styles.colRowRemove} />
        )}
      </View>
      {/* Said in words, not only by a red border: the refusal is a RULE the
          author cannot guess from a colour, and colour is never the only cue.
          Same per-field treatment the two seconds cells already get.
          `accessibilityLiveRegion` on all three (boy-scout, audit 25/08): the
          message appears while the caret is elsewhere, so a reader that is not
          told never learns the cell went red. House precedent is the save
          error in EditorTopBar. */}
      {repsError !== null ? (
        <Text accessibilityLiveRegion="polite" style={styles.validationText}>{repsError}</Text>
      ) : null}
      {timeOpen ? (
        <>
          <View style={styles.chipsLine}>
            <Text style={styles.chipsLabel}>
              {t("program.setBound", { defaultValue: "Bound by" })}
            </Text>
            {/* Grouped, matching PeriodSelector / EffortMeter: without the
                role a reader announces three unrelated radio buttons and the
                "Bound by" caption belongs to none of them. */}
            <View
              accessibilityRole="radiogroup"
              accessibilityLabel={t("program.setBound", {
                defaultValue: "Bound by",
              })}
              style={styles.chipsRow}
            >
              {BOUNDS.map((b) => (
                <Chip
                  key={b.fallback}
                  theme={theme}
                  dense
                  role="radio"
                  label={t(b.key, { defaultValue: b.fallback })}
                  selected={row.workKind === b.kind}
                  // Leaving a duration behind on a set that is no longer a
                  // countdown would re-appear the moment the author tapped
                  // "Time" again, prescribing a bout they had removed.
                  onPress={() =>
                    onUpdateRow({
                      workKind: b.kind,
                      ...(b.kind === "countdown"
                        ? {}
                        : { workDurationSec: undefined }),
                    })
                  }
                />
              ))}
            </View>
          </View>
          {row.workKind === "countdown" ? (
            <View style={styles.chipsLine}>
              <Text style={styles.chipsLabel}>
                {t("program.workSeconds", { defaultValue: "Work (s)" })}
              </Text>
              <View style={styles.chipsRow}>
                <TextInput
                  style={[
                    styles.cellInput,
                    styles.secondsInput,
                    workError !== null && styles.cellInputInvalid,
                  ]}
                  value={row.workDurationSec ?? ""}
                  onChangeText={(v) => onUpdateRow({ workDurationSec: v })}
                  keyboardType="number-pad"
                  accessibilityLabel={t("program.workSeconds", {
                    defaultValue: "Work (s)",
                  })}
                />
                {workError !== null ? (
                  <Text accessibilityLiveRegion="polite" style={styles.validationText}>{workError}</Text>
                ) : null}
              </View>
            </View>
          ) : null}
          {/* Rest is prescribable on ANY set, not just a bout: it is the
              per-set half of the ladder that today only has an exercise-wide
              and a global rung. Left blank, that ladder still decides. */}
          <View style={styles.chipsLine}>
            <Text style={styles.chipsLabel}>
              {t("program.restSeconds", { defaultValue: "Rest (s)" })}
            </Text>
            <View style={styles.chipsRow}>
              <TextInput
                style={[
                  styles.cellInput,
                  styles.secondsInput,
                  restError !== null && styles.cellInputInvalid,
                ]}
                value={row.restSec ?? ""}
                onChangeText={(v) => onUpdateRow({ restSec: v })}
                keyboardType="number-pad"
                placeholder={t("program.restSecondsPlaceholder", {
                  defaultValue: "auto",
                })}
                accessibilityLabel={t("program.restSeconds", {
                  defaultValue: "Rest (s)",
                })}
              />
              {restError !== null ? (
                <Text accessibilityLiveRegion="polite" style={styles.validationText}>{restError}</Text>
              ) : null}
            </View>
          </View>
        </>
      ) : null}
      {noteOpen ? (
        <TextInput
          style={styles.noteInput}
          value={row.note}
          onChangeText={(v) => onUpdateRow({ note: v })}
          placeholder={t("program.setNotesPlaceholder", {
            defaultValue: "Per-set note (optional)",
          })}
          maxLength={200}
          accessibilityLabel={t("composer.setNote", {
            defaultValue: "Set note",
          })}
        />
      ) : null}
    </View>
  );
}
